/**
 * Kendo UI Configuration Module
 *
 * This module handles the configuration of Kendo UI components,
 * including culture settings and global Kendo configuration.
 *
 * @module KendoConfig
 */

import { CONFIG } from '../core/config.js';

/**
 * Initialize Kendo UI with Czech culture
 *
 * @description Sets up Kendo UI with Czech (cs-CZ) culture settings.
 * This ensures that all Kendo components use Czech localization
 * for dates, numbers, and other culture-specific formatting.
 *
 * @function initializeKendo
 * @returns {void}
 *
 * @example
 * // Initialize Kendo with Czech culture
 * KendoConfig.initializeKendo();
 */
export function initializeKendo() {
    if (typeof kendo === 'undefined') {
        // Kendo UI is not available - this is expected in some environments
        return;
    }

    // Set Kendo culture to Czech
    kendo.culture(CONFIG.KENDO.CULTURE);

    // Set default date format for Czech locale
    kendo.culture().calendar.patterns.d = CONFIG.KENDO.DATE_FORMAT;
    kendo.culture().calendar.patterns.D = CONFIG.KENDO.DATETIME_FORMAT;
    kendo.culture().calendar.patterns.t = CONFIG.KENDO.TIME_FORMAT;
    kendo.culture().calendar.patterns.T = CONFIG.KENDO.FULL_TIME_FORMAT;

    // Set number format for Czech locale
    kendo.culture().numberFormat['.'] = ',';
    kendo.culture().numberFormat[','] = ' ';

    // Set global parse formats for DatePicker and DateTimePicker
    // This allows users to enter dates in various formats (with/without spaces, leading zeros)
    // while displaying in the standard format (d. M. yyyy)
    kendo.ui.DatePicker.fn.options.parseFormats = CONFIG.KENDO.DATE_PARSE_FORMATS;
    kendo.ui.DateTimePicker.fn.options.parseFormats = CONFIG.KENDO.DATE_PARSE_FORMATS;
    kendo.ui.TimePicker.fn.options.parseFormats = CONFIG.KENDO.TIME_PARSE_FORMATS;

    // Configure jQuery validation to work with Kendo widgets
    configureValidation();
}

/**
 * Configure jQuery Validation to work with Kendo UI components
 *
 * @description By default, jQuery validation ignores hidden inputs.
 * However, Kendo UI components (DropDownList, NumericTextBox, etc.)
 * create hidden inputs to store their values. This function configures
 * jQuery validation to validate these hidden inputs.
 *
 * @function configureValidation
 * @returns {void}
 *
 * @example
 * // Configure validation for Kendo
 * KendoConfig.configureValidation();
 */
export function configureValidation() {
    if (typeof $ === 'undefined' || typeof $.validator === 'undefined') {
        // jQuery or jQuery validation is not available
        return;
    }

    // Configure jQuery validation to NOT ignore hidden inputs
    // This is necessary for Kendo widgets which create hidden inputs
    $.validator.setDefaults({
        ignore: '' // Don't ignore any fields (including Kendo's hidden inputs)
    });
}

/**
 * Get current Kendo culture information
 *
 * @description Returns information about the currently set Kendo culture.
 * Useful for debugging and verification purposes.
 *
 * @function getCultureInfo
 * @returns {Object} Current culture information
 *
 * @example
 * // Get current culture info
 * const cultureInfo = KendoConfig.getCultureInfo();
 * console.log(cultureInfo);
 */
export function getCultureInfo() {
    return {
        culture: kendo.culture().name,
        calendar: kendo.culture().calendar,
        numberFormat: kendo.culture().numberFormat
    };
}

// Export all functions as default object
export default {
    initializeKendo,
    getCultureInfo,
    configureValidation
};
